import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Table, Row, Col, Input, Modal, TimePicker, Button, Form, Select, message } from "antd";
import toast from "react-hot-toast";
import moment from "moment";

const { Option } = Select;

function Doctorslist() {
  const dispatch = useDispatch();

  // Data & UI state
  const [doctors, setDoctors] = useState([]);
  const [clinicDays, setClinicDays] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Controlled filters & sorting state similar to your simple example
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Modal and form state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [form] = Form.useForm();

  // Fetch clinic days once
  useEffect(() => {
    axios.get("/api/user/get-all-clinic-day")
      .then((res) => {
        if (res.data.success) {
          setClinicDays(res.data.clinicDays);
        } else {
          message.error("Failed to load clinic days");
        }
      })
      .catch(() => {
        message.error("Error fetching clinic days");
      });
  }, []);

  // Fetch doctors data
  const getDoctorData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const response = await axios.get("/api/admin/get-all-doctors", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (response.data.success) {
        setDoctors(response.data.data);
      }
    } catch (error) {
      dispatch(hideLoading());
      console.error(error);
    }
  }, [dispatch]);

  useEffect(() => {
    getDoctorData();
  }, [getDoctorData]);

  // Change doctor status (approve/block)
  const changeDoctorStatus = async (record, status) => {
    try {
      dispatch(showLoading());
      const response = await axios.post(
        "/api/admin/change-doctor-status",
        { doctorId: record._id, userId: record.userId, status },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success(response.data.message);
        getDoctorData();
      }
    } catch (error) {
      toast.error("Something went wrong");
      dispatch(hideLoading());
      console.error(error);
    }
  };

  // Modal open with pre-filled form values
  const openEditModal = (record) => {
    setSelectedDoctor(record);
    form.setFieldsValue({
      clinicDay: record.clinicDay || [],
      feePerConsultation: record.feePerConsultation,
      appointmentTime: record.appointmentTime
        ? [moment(record.appointmentTime[0], "HH:mm"), moment(record.appointmentTime[1], "HH:mm")]
        : [],
    });
    setIsModalVisible(true);
  };

  // Modal submit handler with loading state
  const handleEditSubmit = async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();

      const updatedData = {
        doctorId: selectedDoctor._id,
        clinicDay: values.clinicDay,
        feePerConsultation: values.feePerConsultation,
        appointmentTime: values.appointmentTime
          ? values.appointmentTime.map(time => time.format("HH:mm"))
          : [],
      };

      const response = await axios.post("/api/admin/update-doctor-clinic", updatedData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setModalLoading(false);
      if (response.data.success) {
        toast.success("Doctor Profile Updated  successfully");
        setIsModalVisible(false);
        getDoctorData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setModalLoading(false);
      toast.error("Error Updating Doctor Profile");
      console.error(error);
    }
  };

  // Table change handler for filters and sorting (like your simple example)
  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Clear filters & sorting handlers
  const clearFilters = () => setFilteredInfo({});
  const clearAll = () => {
    setFilteredInfo({});
    setSortedInfo({});
  };

  // Columns with controlled filteredValue & sortOrder, filtering, and sorting logic
  const columns = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "name",
      filteredValue: filteredInfo.name || null,
      sorter: (a, b) => a.firstName.length - b.firstName.length,
      sortOrder: sortedInfo.columnKey === "name" ? sortedInfo.order : null,
      onFilter: (value, record) => `${record.firstName} ${record.lastName}`.toLowerCase().includes(value.toLowerCase()),
      render: (text, record) => (
        <span>{record.firstName} {record.lastName}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      filteredValue: filteredInfo.email || null,
      sorter: (a, b) => a.email.length - b.email.length,
      sortOrder: sortedInfo.columnKey === "email" ? sortedInfo.order : null,
      onFilter: (value, record) => record.email.toLowerCase().includes(value.toLowerCase()),
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Appointment Time",
      dataIndex: "appointmentTime",
      key: "appointmentTime",
      render: (text, record) => (
        <span>
          {record.appointmentTime?.[0] || "N/A"} - {record.appointmentTime?.[1] || "N/A"}
        </span>
      ),
      // No filter or sorter for simplicity here, but can add if needed
    },

      {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      filteredValue: filteredInfo.phoneNumber || null,
      sorter: (a, b) => a.phoneNumber.length - b.phoneNumber.length,
      sortOrder: sortedInfo.columnKey === "phoneNumber" ? sortedInfo.order : null,
      onFilter: (value, record) => record.phoneNumber.includes(value),
      render: (text) => <span>{text}</span>,
    },

      {
      title: "Consultation Fee",
      dataIndex: "feePerConsultation",
      key: "feePerConsultation",
      filteredValue: filteredInfo.feePerConsultation || null,
      sorter: (a, b) => a.feePerConsultation.length - b.feePerConsultation.length,
      sortOrder: sortedInfo.columnKey === "feePerConsultation" ? sortedInfo.order : null,
      onFilter: (value, record) => record.feePerConsultation.includes(value),
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Doctor Clinic Days",
      dataIndex: "clinicDay",
      key: "clinicDay",
      filteredValue: filteredInfo.clinicDay || null,
      onFilter: (value, record) => record.clinicDay?.includes(value),
      sorter: (a, b) => (a.clinicDay?.length || 0) - (b.clinicDay?.length || 0),
      sortOrder: sortedInfo.columnKey === "clinicDay" ? sortedInfo.order : null,
      render: (text, record) => (
        <>
          {record.clinicDay?.map((day, index) => (
            <span key={index} style={{ marginRight: 5, padding: "2px 6px", background: "#f0f0f0", borderRadius: 4 }}>
              {day}
            </span>
          ))}
        </>
      ),
      filters: clinicDays.map(day => ({ text: day.name, value: day.name })),
    },
  
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filteredValue: filteredInfo.status || null,
      filters: [
        { text: "Approved", value: "approved" },
        { text: "Pending", value: "pending" },
        { text: "Blocked", value: "blocked" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span style={{ color: text === "approved" ? "green" : text === "pending" ? "orange" : "red" }}>
          {text.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <div style={{ display: "flex", gap: 10 }}>
          {record.status === "pending" && (
            <Button type="link" onClick={() => changeDoctorStatus(record, "approved")}>
              Approve
            </Button>
          )}
          {record.status === "approved" && (
            <>
              <Button type="link" danger onClick={() => changeDoctorStatus(record, "blocked")}>
                Block
              </Button>
              <Button type="link" onClick={() => openEditModal(record)}>
                Update
              </Button>
            </>
          )}
          {record.status !== "approved" && (
            <Button type="link" onClick={() => openEditModal(record)}>
              Edit
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout>
    
      <Row gutter={20} style={{ marginBottom: 16 }}>
        <Col span={8} xs={24} sm={24} lg={6}>
          <Input
            placeholder="Search doctors..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            allowClear
          />
        </Col>
        <Col>
          <Button onClick={clearFilters} style={{ marginRight: 8 }}>
            Clear filters
          </Button>
          <Button onClick={clearAll}>
            Clear filters and sorters
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={doctors
          .filter(doctor => {
            if (!searchTerm) return true;
            const fullName = `${doctor.firstName} ${doctor.lastName}`.toLowerCase();
            return fullName.includes(searchTerm.toLowerCase());
          })
          .map(doctor => ({ ...doctor, key: doctor._id }))}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1000 }}
        onChange={handleChange}
      />

      <Modal
        title="Update Clinic Days"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)} disabled={modalLoading}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={modalLoading}
            onClick={handleEditSubmit}
          >
            Update
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Choose Clinic Days"
            name="clinicDay"
            rules={[{ required: true, message: "Please select at least one clinic day" }]}
          >
            <Select mode="multiple" allowClear placeholder="Select clinic days">
              {clinicDays.map(day => (
                <Option key={day.name} value={day.name}>
                  {day.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

           <Form.Item
            label="Consultation Fee"
            name="feePerConsultation"
            rules={[{ required: true, message: "Please select fee for Consultation" }]}
          >
             <Input placeholder="Consultation Fee" />
          </Form.Item>

          <Form.Item
            label="Appointment Time"
            name="appointmentTime"
            rules={[{ required: true, message: "Please select appointment time" }]}
          >
            <TimePicker.RangePicker format="HH:mm" className="w-100" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default Doctorslist;
